/**
 * The Kip coop on-disk shapes this PWA cares about.
 *
 * The coop is a folder inside the shared Dropbox App folder (`Apps/Kip-ai/`) —
 * kip-app syncs each graph to `/<graph-name>` there, so the BFF prefixes every
 * path with the chosen coop path. Relative to the coop root:
 *   pages/<ts>-<slug>.md              raw sources in (PWA writes here ONLY)
 *   nest/{entities,concepts,sources}/<slug>.md   the wiki (PWA reads only)
 *   nest/index.md                     generated catalog (ignored)
 *   clucks/YYYY-MM.md                 append-only monthly activity log
 *   .roost/ .henhouse/                never touched (filtered out server-side)
 */

/** Page-type <-> nest subfolder, as the coop lays them out on disk. */
export const TYPE_DIRS = {
  entity: 'entities',
  concept: 'concepts',
  source: 'sources',
} as const;

export type PageType = keyof typeof TYPE_DIRS;

export const DIR_TYPES: Record<string, PageType> = Object.fromEntries(
  Object.entries(TYPE_DIRS).map(([type, dir]) => [dir, type as PageType]),
);

export interface NestPageFrontmatter {
  type?: PageType;
  created?: string; // ISO date YYYY-MM-DD
  updated?: string; // ISO date YYYY-MM-DD
  tags?: string[];
}

/** A parsed nest page as the PWA caches it. */
export interface NestPage {
  /** filename stem — the canonical slug for cache + index keys */
  slug: string;
  /** coop-relative, forward-slashed, e.g. "nest/concepts/sleep-hygiene.md" */
  path: string;
  type: PageType;
  tags: string[];
  created: string;
  updated: string;
  /** markdown body, frontmatter stripped, trimmed */
  body: string;
  /** first prose line, derived when absent (browsing aid; mirrors rebuild-roost) */
  summary: string;
  /** distinct `[[wikilink]]` slugs found in the body */
  links: string[];
  /** Dropbox content hash / rev for delta sync */
  rev?: string;
}

/** One entry in a clucks/YYYY-MM.md file. */
export interface CluckEntry {
  /** YYYY-MM-DD */
  date: string;
  /** hatch | peck | told | groom | reminder | ... (free text upstream) */
  kind: string;
  title: string;
  /** slugs touched by that activity */
  slugs: string[];
}

/** A capture the user typed in the PWA, queued for upload to pages/. */
export interface EggDraft {
  clientId: string;
  slug: string;
  markdown: string;
  createdAt: string;
}

/** A skill-generated file in the coop's exports/ folder (a deck, doc, chart…). */
export interface ExportFile {
  /** coop-relative, forward-slashed, e.g. "exports/q3-report.docx" */
  path: string;
  /** bare filename, e.g. "q3-report.docx" */
  name: string;
  size: number;
  /** Dropbox client_modified (ISO) */
  modified: string;
  /** Dropbox rev for delta sync */
  rev?: string;
}
